import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import EmailLink from '../components/EmailLink';
import PhoneLink from '../components/PhoneLink';
import { useAuth } from '../context/AuthContext';
import { authService, hostelService, userService } from '../services/api';
import { Hostel, User } from '../types';
import UserPhotoCell from '../components/UserPhotoCell';
import HostelCreditsCell from '../components/HostelCreditsCell';
import LoadHostelCreditsModal from '../components/LoadHostelCreditsModal';
import './Dashboard.css';

type FirstAdminStep = { hostelId: number; hostelName: string };

const emptyHostelForm = {
  name: '',
  address: '',
  city: '',
  state: '',
  pincode: '',
  phone: '',
  email: '',
};

const HostelDashboard: React.FC = () => {
  const { user, hostel, selectHostel } = useAuth();
  const [hostels, setHostels] = useState<Hostel[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDrawerOpen, setAnalyticsDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newHostel, setNewHostel] = useState({ ...emptyHostelForm });
  const [firstAdminStep, setFirstAdminStep] = useState<FirstAdminStep | null>(null);
  const [firstAdmin, setFirstAdmin] = useState({
    username: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
  });
  const [firstAdminSubmitting, setFirstAdminSubmitting] = useState(false);

  const [showDeleted, setShowDeleted] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewHostel, setViewHostel] = useState<any>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [editHostel, setEditHostel] = useState<Hostel | null>(null);
  const [editForm, setEditForm] = useState<Partial<Hostel>>({});
  const [passwordHostelId, setPasswordHostelId] = useState<number | null>(null);
  const [hostelUsers, setHostelUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [pwBusy, setPwBusy] = useState<number | null>(null);
  const [pwDraft, setPwDraft] = useState<Record<number, string>>({});

  const [selectedHostelIds, setSelectedHostelIds] = useState<number[]>([]);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapPhone, setMapPhone] = useState('');
  const [mapTargetUser, setMapTargetUser] = useState<User | null>(null);
  const [mapLookupLoading, setMapLookupLoading] = useState(false);
  const [mapSubmitting, setMapSubmitting] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [creditsHostel, setCreditsHostel] = useState<Hostel | null>(null);

  const loadHostels = useCallback(async () => {
    try {
      setLoading(true);
      const data = await hostelService.getAllHostels({
        includeDeleted: user?.userType === 'SUPER_ADMIN' ? showDeleted : false,
      });
      setHostels(data);
    } catch (error) {
      console.error('Failed to load hostels:', error);
      toast.error('Failed to load hostels');
    } finally {
      setLoading(false);
    }
  }, [showDeleted, user?.userType]);

  useEffect(() => {
    loadHostels();
  }, [loadHostels]);

  useEffect(() => {
    if (hostel?.id) {
      loadAnalytics(hostel.id);
      // Super admin: drawer opens only from the Analytics button, not when picking a row.
      if (user?.userType !== 'SUPER_ADMIN') {
        setAnalyticsDrawerOpen(true);
      }
    } else {
      setAnalyticsDrawerOpen(false);
    }
  }, [hostel?.id, user?.userType]);

  useEffect(() => {
    if (!analyticsDrawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAnalyticsDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [analyticsDrawerOpen]);

  useEffect(() => {
    if (analyticsDrawerOpen && hostel) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [analyticsDrawerOpen, hostel]);

  const loadAnalytics = async (hostelId: number) => {
    setAnalyticsLoading(true);
    try {
      const data = await hostelService.getHostelAnalytics(hostelId);
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleCreateHostel = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const created = await hostelService.createHostel(newHostel);
      setNewHostel({ ...emptyHostelForm });
      setShowCreateForm(false);
      await loadHostels();
      const hid = created?.id != null ? Number(created.id) : NaN;
      if (!Number.isFinite(hid)) {
        toast.success('Hostel created. Add an admin from User management if needed.');
        return;
      }
      setFirstAdminStep({ hostelId: hid, hostelName: created.name || 'New hostel' });
      setFirstAdmin({
        username: '',
        email: newHostel.email || '',
        phoneNumber: newHostel.phone || '',
        password: '',
        confirmPassword: '',
      });
      const codeMsg = created.hostelCode ? ` Hostel code: ${created.hostelCode}.` : '';
      toast.success(`Hostel created.${codeMsg} Set up the first hostel admin below (or skip).`);
    } catch (error: any) {
      console.error('Failed to create hostel:', error);
      toast.error(error?.response?.data?.message || 'Failed to create hostel');
    }
  };

  const handleSkipFirstAdmin = () => {
    setFirstAdminStep(null);
    setFirstAdmin({
      username: '',
      email: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
    });
  };

  const handleCreateFirstAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstAdminStep) return;
    if (firstAdmin.password !== firstAdmin.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (firstAdmin.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setFirstAdminSubmitting(true);
    try {
      const adminEmail = firstAdmin.email.trim();
      await authService.signup({
        username: firstAdmin.username.trim(),
        ...(adminEmail ? { email: adminEmail } : {}),
        phoneNumber: firstAdmin.phoneNumber.trim(),
        password: firstAdmin.password,
        hostelId: firstAdminStep.hostelId,
        userType: 'HOSTEL_ADMIN',
        roles: ['HOSTEL_ADMIN'],
      });
      toast.success(
        `Hostel admin created. They can sign in at /login with phone ${firstAdmin.phoneNumber.trim()} and the password you set.`
      );
      handleSkipFirstAdmin();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to create hostel admin');
    } finally {
      setFirstAdminSubmitting(false);
    }
  };

  const handleSelectHostel = (hostelData: Hostel) => {
    selectHostel(hostelData);
  };

  const openView = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    setViewModalOpen(true);
    setViewHostel(null);
    setViewLoading(true);
    try {
      const full = await hostelService.getHostelById(id);
      setViewHostel(full);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to load hostel');
      setViewModalOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const openEdit = (e: React.MouseEvent, h: Hostel) => {
    e.stopPropagation();
    setEditHostel(h);
    setEditForm({ ...h });
  };

  const openLoadCredits = (e: React.MouseEvent, h: Hostel) => {
    e.stopPropagation();
    setCreditsHostel(h);
  };

  const handleCreditsLoaded = (credits: { emailCredits: number; whatsappCredits: number }) => {
    if (!creditsHostel?.id) return;
    const hid = creditsHostel.id;
    setHostels((prev) =>
      prev.map((h) =>
        h.id === hid ? { ...h, emailCredits: credits.emailCredits, whatsappCredits: credits.whatsappCredits } : h,
      ),
    );
    if (hostel?.id === hid) {
      selectHostel({ ...hostel, emailCredits: credits.emailCredits, whatsappCredits: credits.whatsappCredits });
    }
  };

  const handleHostelLogoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !editHostel?.id) return;
    setLogoUploading(true);
    try {
      const updated = await hostelService.uploadHostelLogo(editHostel.id, file);
      toast.success('Logo uploaded');
      setEditHostel(updated);
      setEditForm((prev) => ({ ...prev, logo: updated.logo }));
      await loadHostels();
      if (hostel?.id === updated.id) selectHostel(updated);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Logo upload failed');
    } finally {
      setLogoUploading(false);
    }
  };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editHostel?.id) return;
    try {
      const updated = await hostelService.updateHostel(editHostel.id, editForm);
      toast.success('Hostel updated');
      setEditHostel(null);
      await loadHostels();
      if (hostel?.id === updated.id) selectHostel(updated);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Update failed');
    }
  };

  const setStatus = async (e: React.MouseEvent, h: Hostel, status: Hostel['status']) => {
    e.stopPropagation();
    if (!h.id) return;
    try {
      await hostelService.updateHostel(h.id, { status });
      toast.success(`Status set to ${status}`);
      await loadHostels();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update status');
    }
  };

  const confirmDelete = async (e: React.MouseEvent, h: Hostel) => {
    e.stopPropagation();
    if (!h.id) return;
    if (!window.confirm(`Soft-delete hostel "${h.name}"? It will be hidden from the default list (set inactive).`)) {
      return;
    }
    try {
      await hostelService.deleteHostel(h.id);
      toast.success('Hostel removed');
      if (hostel?.id === h.id) {
        const next = hostels.find((x) => x.id !== h.id);
        if (next) selectHostel(next);
      }
      await loadHostels();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Delete failed');
    }
  };

  const restoreHostel = async (e: React.MouseEvent, h: Hostel) => {
    e.stopPropagation();
    if (!h.id) return;
    try {
      const restored = await hostelService.restoreHostel(h.id);
      toast.success('Hostel restored');
      await loadHostels();
      if (restored?.id) selectHostel(restored);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Restore failed');
    }
  };

  const openPasswordModal = async (e: React.MouseEvent, hostelId: number) => {
    e.stopPropagation();
    setPasswordHostelId(hostelId);
    setPwDraft({});
    setUsersLoading(true);
    try {
      const users = await hostelService.getHostelUsers(hostelId);
      setHostelUsers(users);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to load users');
      setPasswordHostelId(null);
    } finally {
      setUsersLoading(false);
    }
  };

  const submitPasswordReset = async (hostelId: number, u: User) => {
    const pwd = (pwDraft[u.id] || '').trim();
    if (pwd.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setPwBusy(u.id);
    try {
      await hostelService.resetHostelUserPassword(hostelId, u.id, pwd);
      toast.success(`Password updated for ${u.username || u.phoneNumber}`);
      setPwDraft((d) => ({ ...d, [u.id]: '' }));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Password update failed');
    } finally {
      setPwBusy(null);
    }
  };

  const selectableHostelIds = useMemo(
    () => hostels.filter((h) => h.id != null && !h.isDeleted).map((h) => Number(h.id)),
    [hostels]
  );

  const allHostelsSelected =
    selectableHostelIds.length > 0 && selectableHostelIds.every((id) => selectedHostelIds.includes(id));

  const toggleSelectAllHostels = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (!selectableHostelIds.length) return;
    setSelectedHostelIds(e.target.checked ? [...selectableHostelIds] : []);
  };

  const lookupMapUser = async () => {
    const ph = mapPhone.trim();
    if (!ph) {
      toast.error('Enter phone number');
      return;
    }
    setMapLookupLoading(true);
    try {
      const u = await userService.getUserByPhone(encodeURIComponent(ph));
      setMapTargetUser(u);
    } catch (err: any) {
      setMapTargetUser(null);
      toast.error(err?.response?.data?.message || 'User not found');
    } finally {
      setMapLookupLoading(false);
    }
  };

  const submitMapHostels = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mapTargetUser?.id || !selectedHostelIds.length) return;
    setMapSubmitting(true);
    try {
      await hostelService.assignHostelsToUser({
        userId: mapTargetUser.id,
        hostelIds: selectedHostelIds,
      });
      toast.success('Hostels mapped. User can switch property from the Hostel menu in the top bar (or sign in again).');
      setMapModalOpen(false);
      setSelectedHostelIds([]);
      setMapTargetUser(null);
      setMapPhone('');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to map hostels');
    } finally {
      setMapSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading-container">Loading hostels...</div>;
  }

  const occ = analytics?.occupancy;
  const occRateDisplay =
    typeof occ?.occupancyRate === 'string'
      ? occ.occupancyRate
      : `${Number(occ?.occupancyRate ?? analytics?.occupancyRate ?? 0).toFixed(1)}%`;

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Hostel Management Dashboard</h1>
        {(hostel || user?.userType === 'SUPER_ADMIN') && (
          <div className="dashboard-header-actions">
            {hostel && (
              <button
                type="button"
                className="btn btn-primary analytics-header-btn"
                onClick={() => {
                  if (hostel.id) void loadAnalytics(hostel.id);
                  setAnalyticsDrawerOpen(true);
                }}
                title="Open analytics panel"
              >
                Analytics
              </button>
            )}
            {user?.userType === 'SUPER_ADMIN' && (
              <>
                <label className="show-deleted-toggle">
                  <input
                    type="checkbox"
                    checked={showDeleted}
                    onChange={(e) => setShowDeleted(e.target.checked)}
                  />
                  <span>Show deleted</span>
                </label>
                <button className="btn btn-primary" onClick={() => setShowCreateForm(!showCreateForm)}>
                  {showCreateForm ? 'Cancel' : '+ Create New Hostel'}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {firstAdminStep && user?.userType === 'SUPER_ADMIN' && (
        <div className="form-container first-admin-panel">
          <h2>Add first hostel admin</h2>
          <p className="first-admin-lead">
            <strong>{firstAdminStep.hostelName}</strong> is saved. Hostels do not have a login — only people do.
            Create one <strong>hostel admin</strong> so they can sign in with <strong>phone + password</strong> (you stay
            signed in as super admin).
          </p>
          <form onSubmit={handleCreateFirstAdmin}>
            <div className="form-row">
              <div className="form-group">
                <label>Admin display name *</label>
                <input
                  type="text"
                  required
                  value={firstAdmin.username}
                  onChange={(e) => setFirstAdmin({ ...firstAdmin, username: e.target.value })}
                  placeholder="e.g. Priya Manager"
                />
              </div>
              <div className="form-group">
                <label>Email (optional)</label>
                <input
                  type="email"
                  value={firstAdmin.email}
                  onChange={(e) => setFirstAdmin({ ...firstAdmin, email: e.target.value })}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Phone (login id) *</label>
                <input
                  type="tel"
                  required
                  value={firstAdmin.phoneNumber}
                  onChange={(e) => setFirstAdmin({ ...firstAdmin, phoneNumber: e.target.value })}
                  placeholder="10-digit mobile"
                />
              </div>
              <div className="form-group">
                <label>Temporary password *</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  value={firstAdmin.password}
                  onChange={(e) => setFirstAdmin({ ...firstAdmin, password: e.target.value })}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Confirm password *</label>
              <input
                type="password"
                required
                minLength={6}
                autoComplete="new-password"
                value={firstAdmin.confirmPassword}
                onChange={(e) => setFirstAdmin({ ...firstAdmin, confirmPassword: e.target.value })}
              />
            </div>
            <div className="first-admin-actions">
              <button type="submit" className="btn btn-success" disabled={firstAdminSubmitting}>
                {firstAdminSubmitting ? 'Creating…' : 'Create hostel admin'}
              </button>
              <button
                type="button"
                className="btn btn-warning"
                onClick={handleSkipFirstAdmin}
                disabled={firstAdminSubmitting}
              >
                Skip for now
              </button>
            </div>
          </form>
        </div>
      )}

      {showCreateForm && (
        <div className="form-container">
          <h2>Create New Hostel</h2>
          <form onSubmit={handleCreateHostel}>
            <div className="form-group">
              <label>Hostel Name *</label>
              <input
                type="text"
                required
                value={newHostel.name}
                onChange={(e) => setNewHostel({ ...newHostel, name: e.target.value })}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Address *</label>
                <input
                  type="text"
                  required
                  value={newHostel.address}
                  onChange={(e) => setNewHostel({ ...newHostel, address: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  required
                  value={newHostel.city}
                  onChange={(e) => setNewHostel({ ...newHostel, city: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  value={newHostel.state}
                  onChange={(e) => setNewHostel({ ...newHostel, state: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Pincode</label>
                <input
                  type="text"
                  value={newHostel.pincode}
                  onChange={(e) => setNewHostel({ ...newHostel, pincode: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={newHostel.phone}
                  onChange={(e) => setNewHostel({ ...newHostel, phone: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={newHostel.email}
                  onChange={(e) => setNewHostel({ ...newHostel, email: e.target.value })}
                />
              </div>
            </div>

            <button type="submit" className="btn btn-success">
              Create Hostel
            </button>
          </form>
        </div>
      )}

      <div className="hostels-table-section">
        {user?.userType === 'SUPER_ADMIN' && hostels.length > 0 && (
          <div className="hostels-table-toolbar">
            <button
              type="button"
              className="btn btn-primary"
              disabled={selectedHostelIds.length === 0}
              onClick={() => {
                setMapPhone('');
                setMapTargetUser(null);
                setMapModalOpen(true);
              }}
            >
              Map to user{selectedHostelIds.length ? ` (${selectedHostelIds.length})` : ''}
            </button>
            {selectedHostelIds.length > 0 && (
              <button type="button" className="btn btn-sm btn-warning" onClick={() => setSelectedHostelIds([])}>
                Clear selection
              </button>
            )}
            <span className="hostels-toolbar-hint">Select hostels below, then map them to one HOSTEL_ADMIN or STAFF user.</span>
          </div>
        )}
        {hostels.length === 0 ? (
          <p className="hostels-table-empty">No hostels found. Create one to get started!</p>
        ) : (
          <div className="hostels-table-wrap">
            <table className="hostels-data-table">
              <thead>
                <tr>
                  {user?.userType === 'SUPER_ADMIN' && (
                    <th className="col-check" title="Select hostels to map to one user">
                      <input
                        type="checkbox"
                        checked={allHostelsSelected}
                        onChange={toggleSelectAllHostels}
                        aria-label="Select all hostels"
                      />
                    </th>
                  )}
                  <th className="col-code">Code</th>
                  <th className="col-logo">Logo</th>
                  <th className="col-name">Hostel</th>
                  <th className="col-location">Location</th>
                  <th className="col-phone">Phone</th>
                  <th className="col-status">Status</th>
                  <th className="col-credits">Credits</th>
                  {user?.userType === 'SUPER_ADMIN' && <th className="col-actions">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {hostels.map((h) => {
                  const statusLabel = h.isDeleted ? 'DELETED' : h.status || 'ACTIVE';
                  const statusClass = h.isDeleted ? 'deleted' : (h.status || 'ACTIVE').toLowerCase();
                  const isSelected = hostel?.id === h.id;
                  return (
                    <tr
                      key={h.id}
                      className={`hostel-table-row ${isSelected ? 'hostel-table-row-selected' : ''} ${h.isDeleted ? 'hostel-table-row-deleted' : ''}`}
                      onClick={() => handleSelectHostel(h)}
                      title="Click row to use this hostel for analytics below"
                    >
                      {user?.userType === 'SUPER_ADMIN' && (
                        <td
                          className="col-check"
                          onClick={(ev) => {
                            ev.stopPropagation();
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedHostelIds.includes(h.id!)}
                            onChange={(e) => {
                              e.stopPropagation();
                              const hid = h.id!;
                              setSelectedHostelIds((prev) =>
                                prev.includes(hid) ? prev.filter((x) => x !== hid) : [...prev, hid]
                              );
                            }}
                            aria-label={`Select ${h.name}`}
                          />
                        </td>
                      )}
                      <td className="col-code">
                        <span className="hostel-code-pill" title="4-digit hostel code">
                          {h.hostelCode || '—'}
                        </span>
                      </td>
                      <td className="col-logo" onClick={(ev) => ev.stopPropagation()}>
                        <div className="hostel-table-logo-cell">
                          {h.logo ? (
                            <img src={h.logo} alt="" className="hostel-table-logo-img" />
                          ) : (
                            <span className="hostel-table-logo-fallback" aria-hidden>
                              🏢
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="col-name">
                        <span className="hostel-table-name">{h.name}</span>
                        {isSelected && <span className="hostel-table-current-badge">Current</span>}
                      </td>
                      <td className="col-location">
                        {[h.city, h.state].filter(Boolean).join(', ') || '—'}
                      </td>
                      <td className="col-phone">
                        <PhoneLink phone={h.phone} />
                      </td>
                      <td className="col-status">
                        <span className={`status-badge status-badge-table ${statusClass}`}>{statusLabel}</span>
                      </td>
                      <td className="col-credits">
                        <HostelCreditsCell
                          emailCredits={h.emailCredits}
                          whatsappCredits={h.whatsappCredits}
                        />
                      </td>
                      {user?.userType === 'SUPER_ADMIN' && h.id != null && (
                        <td className="col-actions" onClick={(ev) => ev.stopPropagation()}>
                          <div className="hostel-actions-cell" role="group">
                            <button type="button" className="btn btn-sm btn-primary" onClick={(ev) => openView(ev, h.id!)}>
                              View
                            </button>
                            <button type="button" className="btn btn-sm btn-primary" onClick={(ev) => openEdit(ev, h)}>
                              Edit
                            </button>
                            <button type="button" className="btn btn-sm btn-secondary" onClick={(ev) => openLoadCredits(ev, h)}>
                              Load credits
                            </button>
                            <button type="button" className="btn btn-sm btn-success" onClick={(ev) => setStatus(ev, h, 'ACTIVE')}>
                              Active
                            </button>
                            <button type="button" className="btn btn-sm btn-warning" onClick={(ev) => setStatus(ev, h, 'INACTIVE')}>
                              Inactive
                            </button>
                            <button type="button" className="btn btn-sm btn-danger" onClick={(ev) => setStatus(ev, h, 'SUSPENDED')}>
                              Suspend
                            </button>
                            <button type="button" className="btn btn-sm btn-primary" onClick={(ev) => openPasswordModal(ev, h.id!)}>
                              Passwords
                            </button>
                            {!h.isDeleted ? (
                              <button type="button" className="btn btn-sm btn-danger" onClick={(ev) => confirmDelete(ev, h)}>
                                Delete
                              </button>
                            ) : (
                              <button type="button" className="btn btn-sm btn-success" onClick={(ev) => restoreHostel(ev, h)}>
                                Restore
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {hostel && (
        <>
          <div
            className={`analytics-drawer-backdrop ${analyticsDrawerOpen ? 'is-visible' : ''}`}
            aria-hidden={!analyticsDrawerOpen}
            onClick={() => setAnalyticsDrawerOpen(false)}
          />
          <aside
            className={`analytics-drawer ${analyticsDrawerOpen ? 'is-open' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="analytics-drawer-title"
            aria-hidden={!analyticsDrawerOpen}
          >
            <div className="analytics-drawer-header">
              <h2 id="analytics-drawer-title">Analytics</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setAnalyticsDrawerOpen(false)}
                aria-label="Close analytics"
              >
                ×
              </button>
            </div>
            <div className="analytics-drawer-body">
              {analyticsLoading ? (
                <p className="analytics-drawer-loading">Loading metrics…</p>
              ) : (
                <div className="analytics-grid analytics-grid-drawer">
                  <div className="analytics-card">
                    <h4>Total Occupancy</h4>
                    <div className="stat-value">{occRateDisplay}</div>
                  </div>

                  <div className="analytics-card">
                    <h4>Active Tenants</h4>
                    <div className="stat-value">{analytics?.tenants?.activeTenants ?? analytics?.activeTenants ?? 0}</div>
                  </div>

                  <div className="analytics-card">
                    <h4>Pending Approvals</h4>
                    <div className="stat-value">{analytics?.pendingOnboardings ?? 0}</div>
                  </div>

                  <div className="analytics-card">
                    <h4>Open Complaints</h4>
                    <div className="stat-value">
                      {analytics?.complaints?.openComplaints ?? analytics?.openComplaints ?? 0}
                    </div>
                  </div>

                  <div className="analytics-card">
                    <h4>Monthly Revenue</h4>
                    <div className="stat-value">
                      ₹{(analytics?.revenue?.totalRevenue ?? analytics?.monthlyRevenue ?? 0).toLocaleString()}
                    </div>
                  </div>

                  <div className="analytics-card">
                    <h4>Available Beds</h4>
                    <div className="stat-value">{analytics?.occupancy?.availableBeds ?? analytics?.availableBeds ?? 0}</div>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </>
      )}

      {viewModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="view-hostel-title">
          <div className="modal-panel modal-panel-wide">
            <div className="modal-header">
              <h2 id="view-hostel-title">{viewHostel?.name || 'Hostel'}</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => {
                  setViewModalOpen(false);
                  setViewHostel(null);
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {viewLoading ? (
                <p>Loading…</p>
              ) : (
                <pre className="hostel-view-json">{JSON.stringify(viewHostel, null, 2)}</pre>
              )}
            </div>
          </div>
        </div>
      )}

      {editHostel && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-panel">
            <div className="modal-header">
              <h2>Edit hostel</h2>
              <button type="button" className="modal-close" onClick={() => setEditHostel(null)} aria-label="Close">
                ×
              </button>
            </div>
            <form className="modal-body" onSubmit={saveEdit}>
              {editHostel.hostelCode && (
                <div className="form-group">
                  <label>4-digit code</label>
                  <input type="text" readOnly value={editHostel.hostelCode} className="hostel-code-readonly" />
                  <small className="text-muted">Assigned when the hostel was created; cannot be changed.</small>
                </div>
              )}
              <div className="form-group hostel-edit-logo">
                <label>Logo</label>
                <div className="hostel-edit-logo-row">
                  <div className="hostel-edit-logo-preview">
                    {editForm.logo || editHostel.logo ? (
                      <img
                        src={editForm.logo || editHostel.logo || ''}
                        alt=""
                        className="hostel-edit-logo-img"
                      />
                    ) : (
                      <span className="hostel-table-logo-fallback" aria-hidden>
                        🏢
                      </span>
                    )}
                  </div>
                  <div className="hostel-edit-logo-actions">
                    <input
                      type="file"
                      id="hostel-logo-upload"
                      accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
                      className="hostel-logo-file-input"
                      disabled={logoUploading}
                      onChange={handleHostelLogoFile}
                    />
                    <label
                      htmlFor="hostel-logo-upload"
                      className={`btn btn-sm btn-primary hostel-logo-upload-label ${logoUploading ? 'is-busy' : ''}`}
                    >
                      {logoUploading ? 'Uploading…' : 'Upload logo'}
                    </label>
                    <small className="text-muted">
                      JPEG, PNG, GIF, WebP. Image is uploaded to cloud (same service as profile photos).
                    </small>
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  required
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Address *</label>
                <input
                  type="text"
                  required
                  value={editForm.address || ''}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={editForm.city || ''}
                    onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    value={editForm.state || ''}
                    onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Pincode</label>
                  <input
                    type="text"
                    value={editForm.pincode || ''}
                    onChange={(e) => setEditForm({ ...editForm, pincode: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={editForm.status || 'ACTIVE'}
                    onChange={(e) =>
                      setEditForm({ ...editForm, status: e.target.value as Hostel['status'] })
                    }
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                    <option value="SUSPENDED">SUSPENDED</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={editForm.phone || ''}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editForm.email || ''}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>GST number</label>
                <input
                  type="text"
                  value={editForm.gstNumber || ''}
                  onChange={(e) => setEditForm({ ...editForm, gstNumber: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Subscription plan</label>
                <select
                  value={editForm.subscriptionPlan || 'BASIC'}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      subscriptionPlan: e.target.value as Hostel['subscriptionPlan'],
                    })
                  }
                >
                  <option value="BASIC">BASIC</option>
                  <option value="PRO">PRO</option>
                  <option value="ENTERPRISE">ENTERPRISE</option>
                </select>
              </div>
              <div className="modal-footer-btns">
                <button type="button" className="btn btn-warning" onClick={() => setEditHostel(null)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-success">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {mapModalOpen && user?.userType === 'SUPER_ADMIN' && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="map-hostels-title">
          <div className="modal-panel modal-panel-wide">
            <div className="modal-header">
              <h2 id="map-hostels-title">Map hostels to user</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => {
                  setMapModalOpen(false);
                  setMapTargetUser(null);
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <form className="modal-body" onSubmit={submitMapHostels}>
              <p className="modal-hint">
                Merges the selected hostels into that user&apos;s access list (hostel admin or staff only). They can
                switch properties from the <strong>Hostel</strong> menu in the top bar after signing in.
              </p>
              <div className="form-group">
                <label>Selected hostels</label>
                <ul className="map-selected-hostels-list">
                  {selectedHostelIds.map((id) => {
                    const name = hostels.find((x) => x.id === id)?.name || String(id);
                    return <li key={id}>{name}</li>;
                  })}
                </ul>
              </div>
              <div className="form-row map-lookup-row">
                <div className="form-group">
                  <label>User phone (login id)</label>
                  <input
                    type="tel"
                    value={mapPhone}
                    onChange={(e) => setMapPhone(e.target.value)}
                    placeholder="10-digit mobile"
                    autoComplete="tel"
                  />
                </div>
                <div className="form-group map-lookup-actions">
                  <label className="map-lookup-spacer">&nbsp;</label>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={mapLookupLoading}
                    onClick={() => lookupMapUser()}
                  >
                    {mapLookupLoading ? 'Searching…' : 'Look up'}
                  </button>
                </div>
              </div>
              {mapTargetUser && (
                <div className="map-user-preview">
                  <h3 className="map-login-details-title">Login &amp; account</h3>
                  <dl className="map-login-details">
                    <div className="map-login-row">
                      <dt>Sign-in URL</dt>
                      <dd>
                        <code className="map-login-code">/login</code>
                      </dd>
                    </div>
                    <div className="map-login-row">
                      <dt>Login ID (phone)</dt>
                      <dd>
                        <PhoneLink phone={mapTargetUser.phoneNumber} className="map-login-phone" />
                      </dd>
                    </div>
                    <div className="map-login-row">
                      <dt>Password</dt>
                      <dd className="map-login-password-note">
                        Not shown here. Use <strong>Passwords</strong> on a hostel row to set or reset it. User signs in
                        with phone + that password.
                      </dd>
                    </div>
                    <div className="map-login-row">
                      <dt>Display name</dt>
                      <dd>{mapTargetUser.username || '—'}</dd>
                    </div>
                    <div className="map-login-row">
                      <dt>Email</dt>
                      <dd>
                        <EmailLink email={mapTargetUser.email} />
                      </dd>
                    </div>
                    <div className="map-login-row">
                      <dt>User ID</dt>
                      <dd>
                        <code className="map-login-code">{mapTargetUser.id}</code>
                      </dd>
                    </div>
                    <div className="map-login-row">
                      <dt>Role</dt>
                      <dd>{mapTargetUser.userType || '—'}</dd>
                    </div>
                    {mapTargetUser.roles && mapTargetUser.roles.length > 0 && (
                      <div className="map-login-row">
                        <dt>Roles (JWT)</dt>
                        <dd>{mapTargetUser.roles.join(', ')}</dd>
                      </div>
                    )}
                    <div className="map-login-row">
                      <dt>Current hostel access</dt>
                      <dd>
                        {(mapTargetUser.hostelIds && mapTargetUser.hostelIds.length > 0
                          ? mapTargetUser.hostelIds.join(', ')
                          : mapTargetUser.hostelId) ?? '—'}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}
              <div className="modal-footer-btns">
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={() => {
                    setMapModalOpen(false);
                    setMapTargetUser(null);
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-success" disabled={!mapTargetUser || mapSubmitting}>
                  {mapSubmitting ? 'Saving…' : 'Assign hostels'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {passwordHostelId != null && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-panel modal-panel-wide">
            <div className="modal-header">
              <h2>Reset user passwords</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setPasswordHostelId(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-hint">
                Set a new login password for staff tied to this hostel. They sign in with <strong>phone number</strong>{' '}
                + the password you set here.
              </p>
              {usersLoading ? (
                <p>Loading users…</p>
              ) : hostelUsers.length === 0 ? (
                <p>No users found for this hostel yet.</p>
              ) : (
                <table className="hostel-users-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Type</th>
                      <th>New password</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {hostelUsers.map((u) => (
                      <tr key={u.id}>
                        <td>
                          <div className="user-list-name-cell">
                            <UserPhotoCell name={u.username} photoUrl={u.photo} size="sm" />
                            <span title={u.username}>{u.username || '—'}</span>
                          </div>
                        </td>
                        <td>
                          <PhoneLink phone={u.phoneNumber} />
                        </td>
                        <td>{u.userType}</td>
                        <td>
                          <input
                            type="password"
                            autoComplete="new-password"
                            placeholder="min 6 chars"
                            value={pwDraft[u.id] || ''}
                            onChange={(e) => setPwDraft({ ...pwDraft, [u.id]: e.target.value })}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            disabled={pwBusy === u.id}
                            onClick={() => submitPasswordReset(passwordHostelId, u)}
                          >
                            {pwBusy === u.id ? '…' : 'Update'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {creditsHostel && (
        <LoadHostelCreditsModal
          hostel={creditsHostel}
          onClose={() => setCreditsHostel(null)}
          onSuccess={handleCreditsLoaded}
        />
      )}
    </div>
  );
};

export default HostelDashboard;
